import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { ConfirmDialog } from "../../components/Dialog";
import { ErrorState } from "../../components/ErrorState";
import { ListSkeleton } from "../../components/Loading";
import { Pager } from "../../components/Pager";
import { useToast } from "../../components/Toast";
import { api, type AdminColumn, type AdminRow, type AdminTable, type RowKey } from "../../lib/api";
import { useAdminMutation, useAdminRows, useAdminTables, useDebounced } from "../../lib/queries";
import { useTitle } from "../../lib/useTitle";
import { RowDialog } from "./RowDialog";

const PAGE_SIZE = 50;
const number = new Intl.NumberFormat("fr-FR");
const dateTime = new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" });

/** What each table holds, for admins who do not know the schema by heart. */
const DESCRIPTIONS: Record<string, string> = {
  app_settings: "Réglages modifiables depuis l'administration (inscriptions).",
  categories: "Catégories créées par les utilisateurs.",
  companies: "Entreprises connues, suivies ou non.",
  events: "Événements détectés par les sources (annonces, jugements, changements…).",
  import_jobs: "Imports de fichiers, avec leur progression.",
  login_tokens: "Liens de connexion (empreintes seulement).",
  notifications: "Événements déjà traités pour chaque surveillance (envoyés ou ignorés).",
  source_states: "État de chaque source par entreprise : curseur, instantané, dernière erreur.",
  subscribers: "Comptes utilisateurs.",
  watch_categories: "Catégories de chaque surveillance.",
  watches: "Entreprises suivies par chaque utilisateur.",
};

export function whereLink(table: string, where: Record<string, unknown>) {
  return `/admin/donnees/${table}?where=${encodeURIComponent(JSON.stringify(where))}`;
}

export function AdminData() {
  const { table } = useParams();
  const tables = useAdminTables();
  useTitle(table ? `Table ${table}` : "Données");

  if (tables.isError) return <ErrorState error={tables.error} onRetry={() => tables.refetch()} />;
  if (!tables.data) return <ListSkeleton />;
  if (!table) return <TableList tables={tables.data} />;
  const info = tables.data.find((t) => t.name === table);
  if (!info) return <p className="notice notice--error">Table inconnue : {table}.</p>;
  // Remount on table change so filters and pages do not leak between tables.
  return <TableView key={table} table={info} />;
}

function TableList({ tables }: { tables: AdminTable[] }) {
  return (
    <section aria-labelledby="tables-title">
      <h2 id="tables-title">Tables</h2>
      <p className="admin-hint">
        Accès direct à la base : les modifications ne passent pas par les règles de l'application (e-mails, dates de
        début des alertes…). Préférez l'onglet Utilisateurs pour gérer les comptes.
      </p>
      <ul className="table-cards">
        {tables.map((t) => (
          <li key={t.name}>
            <Link className="table-card" to={`/admin/donnees/${t.name}`}>
              <span className="table-card__name">{t.name}</span>
              <span className="table-card__count">
                {number.format(t.row_count)} ligne{t.row_count > 1 ? "s" : ""}
              </span>
              {DESCRIPTIONS[t.name] && <span className="table-card__description">{DESCRIPTIONS[t.name]}</span>}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function parseWhere(raw: string | null): Record<string, unknown> {
  try {
    const value: unknown = JSON.parse(raw ?? "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function TableView({ table }: { table: AdminTable }) {
  const [params, setParams] = useSearchParams();
  const where = parseWhere(params.get("where"));
  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim());
  const [sort, setSort] = useState<{ column: string | null; desc: boolean }>({ column: null, desc: true });
  const [page, setPage] = useState(1);
  const rows = useAdminRows(table.name, { q, where, sort: sort.column, desc: sort.desc, page });
  const [editing, setEditing] = useState<RowKey | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminRow | null>(null);
  const toast = useToast();
  const remove = useAdminMutation((key: RowKey) => api.admin.deleteRow(table.name, key));

  const removeFilter = (column: string) => {
    const next = { ...where };
    delete next[column];
    setPage(1);
    setParams(Object.keys(next).length ? { where: JSON.stringify(next) } : {}, { replace: true });
  };
  const sortBy = (column: string) => {
    setPage(1);
    setSort((s) => (s.column === column ? { column, desc: !s.desc } : { column, desc: false }));
  };
  const sortedBy = sort.column ?? (table.primary_key.length === 1 ? table.primary_key[0] : null);

  return (
    <section aria-labelledby="table-title">
      <p className="back-link">
        <Link to="/admin/donnees">← Toutes les tables</Link>
      </p>
      <div className="admin-head">
        <div>
          <h2 id="table-title">{table.name}</h2>
          {DESCRIPTIONS[table.name] && <p className="admin-hint">{DESCRIPTIONS[table.name]}</p>}
        </div>
        <button type="button" className="button button--small" onClick={() => setEditing("new")}>
          Ajouter une ligne
        </button>
      </div>

      <div className="toolbar">
        <div className="chips" aria-label="Filtres actifs">
          {Object.entries(where).map(([column, value]) => (
            <span key={column} className="chip">
              {column} = {JSON.stringify(value)}
              <button type="button" aria-label={`Retirer le filtre sur ${column}`} onClick={() => removeFilter(column)}>
                ×
              </button>
            </span>
          ))}
        </div>
        <input
          className="input"
          type="search"
          aria-label={`Rechercher dans ${table.name}`}
          placeholder="Rechercher (texte ou identifiant)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {rows.isError ? (
        <div className="notice notice--error" role="alert">
          {rows.error.message}
        </div>
      ) : !rows.data ? (
        <ListSkeleton />
      ) : rows.data.rows.length === 0 ? (
        <p className="empty">Aucune ligne.</p>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table admin-table admin-table--raw">
              <thead>
                <tr>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                  {table.columns.map((c) => (
                    <th
                      key={c.name}
                      scope="col"
                      aria-sort={sortedBy === c.name ? (sort.desc ? "descending" : "ascending") : undefined}
                    >
                      <button type="button" className="sort-button" onClick={() => sortBy(c.name)} title={columnHint(c)}>
                        {c.name}
                        {c.primary_key && <span className="admin-table__pk"> clé</span>}
                        {sortedBy === c.name && <span aria-hidden="true">{sort.desc ? " ↓" : " ↑"}</span>}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.data.rows.map((row) => (
                  <tr key={JSON.stringify(row.key)}>
                    <td className="admin-table__actions">
                      <button type="button" className="button button--link" onClick={() => setEditing(row.key)}>
                        Modifier
                      </button>
                      <button type="button" className="button button--link is-negative" onClick={() => setDeleting(row)}>
                        Supprimer
                      </button>
                    </td>
                    {table.columns.map((c) => (
                      <td key={c.name} className={c.kind === "integer" || c.kind === "number" ? "num" : undefined}>
                        <Cell column={c} value={row.values[c.name]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} pageSize={PAGE_SIZE} total={rows.data.total} onPage={setPage} />
        </>
      )}

      {editing !== null && (
        <RowDialog
          table={table}
          rowKey={editing === "new" ? null : editing}
          prefill={editing === "new" ? where : undefined}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Supprimer cette ligne ?"
        body={
          deleting
            ? `${table.name} ${JSON.stringify(deleting.key)} sera supprimée, ainsi que les lignes qui en dépendent quand la base le prévoit. Cette action est définitive.`
            : ""
        }
        confirmLabel="Supprimer"
        danger
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(deleting!.key, {
            onSuccess: (r) => {
              toast(r.message);
              setDeleting(null);
            },
            onError: (e) => toast(e.message, "error"),
          })
        }
      />
    </section>
  );
}

function columnHint(c: AdminColumn) {
  const parts = [c.kind, c.nullable ? "facultatif" : "obligatoire"];
  if (c.max_length) parts.push(`${c.max_length} car. max`);
  if (c.foreign_key) parts.push(`→ ${c.foreign_key}`);
  return parts.join(" · ");
}

function Cell({ column, value }: { column: AdminColumn; value: unknown }) {
  if (value === null || value === undefined) return <span className="null">NULL</span>;
  if (column.foreign_key) {
    const [table = "", target = ""] = column.foreign_key.split(".");
    return <Link to={whereLink(table, { [target]: value })}>{String(value)}</Link>;
  }
  if (column.kind === "boolean") return <>{value ? "oui" : "non"}</>;
  if (column.kind === "datetime" && typeof value === "string") {
    const date = new Date(value);
    return (
      <time dateTime={value} className="nowrap">
        {Number.isNaN(date.getTime()) ? value : dateTime.format(date)}
      </time>
    );
  }
  if (column.kind === "json" || column.kind === "text") return <span className="admin-table__long">{String(value)}</span>;
  return <>{String(value)}</>;
}
